import React, { SyntheticEvent, useEffect, useMemo } from "react";
import { Card, Col, FormControl, FormGroup, FormLabel, Nav, Tab } from "react-bootstrap";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import { mapLocaleToCountry } from "../../../witadmin/Common/utils";
import { HtmlEditorWidget } from "../html";
import { messages } from "./messages";
import { ErrorField, StyledContainer, StyledTranslationCard, WitLabel } from "./styles";
import "flag-icon-css/css/flag-icon.css";

export interface Translation {
  locale: string;
  description: string;
  name: string;
}

export function getLocaleCountryMap(locales: string[]): Record<string, string> {
  return locales.reduce<Record<string, string>>((prev, locale) => {
    prev[locale] = mapLocaleToCountry(locale);
    return prev;
  }, {});
}

export interface TranslationForm {
  translationsList: Translation[];
}

export const TranslationComponent = ({
  allowedLocales,
  defaultValues,
  disabled = false,
}: {
  allowedLocales: string[];
  disabled?: boolean;
  defaultValues?: Translation[];
}) => {
  const localeCountryMap = useMemo(() => getLocaleCountryMap(allowedLocales), [allowedLocales]);

  const {
    control,
    register,
    formState: { errors },
    trigger,
    setValue,
  } = useFormContext<TranslationForm>();
  const { append } = useFieldArray({
    control,
    name: "translationsList",
  });

  useEffect(() => {
    if (defaultValues) {
      append(defaultValues);
    }
  }, [defaultValues, append]);

  const tabs = allowedLocales?.map((locale: string, index: number) => {
    const { onBlur } = register(`translationsList.${index}.name`, {
      required: true,
    });

    return [
      <Nav.Item key={locale}>
        <Nav.Link eventKey={`locale-${locale}`}>
          <span className={`flag-icon flag-icon-${localeCountryMap[locale]}`} />
        </Nav.Link>
      </Nav.Item>,
      <Tab.Pane key={locale} eventKey={`locale-${locale}`}>
        <FormGroup as={Col}>
          <FormLabel column sm={2}>
            <FormattedMessage {...messages.name} />
            {"*"}
          </FormLabel>

          <Col sm={6}>
            <FormControl
              disabled={disabled}
              {...register(`translationsList.${index}.name`, {
                required: {
                  value: true,
                  message: "",
                },
              })}
              onBlur={(event: SyntheticEvent) => {
                onBlur(event);
                trigger(`translationsList.${index}.name`);
                setValue(`translationsList.${index}.locale`, locale);
              }}
            />

            {errors?.translationsList &&
              errors?.translationsList[index]?.name?.type === "required" && (
                <ErrorField>
                  <FormattedMessage {...messages.presenceValidator} />
                </ErrorField>
              )}
          </Col>
        </FormGroup>
        <FormGroup as={Col}>
          <FormLabel column sm={2}>
            <FormattedMessage {...messages.description} />
            {"*"}
          </FormLabel>
          <Col sm={12}>
            <Controller
              name={`translationsList.${index}.description`}
              control={control}
              rules={{
                required: {
                  value: true,
                  message: "",
                },
              }}
              render={({ field }) => (
                <HtmlEditorWidget
                  disabled={disabled}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            {errors?.translationsList &&
              errors?.translationsList[index]?.description?.type === "required" && (
                <ErrorField>
                  <FormattedMessage {...messages.presenceValidator} />
                </ErrorField>
              )}
          </Col>
        </FormGroup>
      </Tab.Pane>,
    ];
  });

  return (
    <StyledContainer>
      <WitLabel>
        <FormattedMessage {...messages.translations} />
      </WitLabel>

      {!!errors?.translationsList && (
        <ErrorField className="mb-2">
          <FormattedMessage {...messages.tabsErrors} />
        </ErrorField>
      )}

      <Tab.Container defaultActiveKey={`locale-${allowedLocales[0]}`}>
        <StyledTranslationCard>
          <Card.Header>
            <Nav variant="tabs">{tabs.map((t: any) => t[0])}</Nav>
          </Card.Header>
          <Card.Body>
            <Tab.Content>{tabs.map((t: any) => t[1])}</Tab.Content>
          </Card.Body>
        </StyledTranslationCard>
      </Tab.Container>
    </StyledContainer>
  );
};
